import React, { useEffect, useRef, useState } from "react"
import Header from "./Header"
import WeekDays from "./WeekDays"
import { motion, AnimatePresence } from "framer-motion"
import Grid from "./Grid"
import { useTranslation } from "react-i18next"
import { ddmmyyyyformatDate, strToddmmyyyyDate } from "../../utils/date/format"
import { useClickOutside } from "../../hooks"
import {
  CALENDAR_FIX_MODE,
  CALENDAR_FLEX_MODE,
  LEFT_CALENDAR,
  RIGHT_CALENDAR,
} from "../../utils/variables/calendar"

const Calendar = ({
  datesState,
  isDeparture = true,
  currencySymbol,
  priceData = {},
  highlightedDates = {},
  onDatesChange,
  availableDepart = [],
  availableRetur = [],
  from,
  to,
  mode,
  maxDatesRange = 15,
  departPriceData = {},
  returPriceData = {},
  onCalendarMonthChange,
}) => {
  const { t } = useTranslation()
  const months = t("calendar.months", { returnObjects: true })
  const weekDays = t("calendar.week_days", { returnObjects: true })

  const currentDates = isDeparture ? from : to
  const safeCurrentDates = Array.isArray(currentDates) ? currentDates : []

  const [currentMonth, setCurrentMonth] = useState(
    safeCurrentDates.length ? strToddmmyyyyDate(safeCurrentDates[0]) : new Date()
  )
  const [openMonthPicker, setOpenMonthPicker] = useState(null)
  const [lastClick, setLastClick] = useState({ date: null, timestamp: 0 })
  const [selectedDates, setSelectedDates] = useState(safeCurrentDates)
  const [clickCount, setClickCount] = useState(0)
  const [dragStartDate, setDragStartDate] = useState(null)
  const [direction, setDirection] = useState(0)
  const [isSelecting, setIsSelecting] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  const datePickerSect = useRef(null)

  useClickOutside(datePickerSect, () => setOpenMonthPicker(false))

  useEffect(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth() + 1
    onCalendarMonthChange?.({ year, month })

    const nextMonthDate = new Date(year, month, 1)
    onCalendarMonthChange?.({
      year: nextMonthDate.getFullYear(),
      month: nextMonthDate.getMonth() + 1,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setClickCount(0)
    setLastClick({ date: null, timestamp: 0 })
    setIsSelecting(false)
    setDragStartDate(null)
    setSelectedDates(isDeparture ? datesState.from || [] : datesState.to || [])

    const nextMode = isDeparture ? CALENDAR_FLEX_MODE : CALENDAR_FIX_MODE
    notify(datesState.from || [], datesState.to || [], nextMode)
  }, [isDeparture])

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640)
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  const notify = (newFrom, newTo, newMode) => {
    if (!onDatesChange) return
    const hasFrom = newFrom?.[0]?.trim?.() !== ""
    const hasTo = newTo?.[0]?.trim?.() !== ""
    onDatesChange({
      from: newFrom,
      to: newTo,
      mode: newMode,
      round_trip: hasFrom && hasTo,
      one_way: hasFrom && !hasTo,
    })
  }

  const changeMonth = (delta) => {
    setDirection(delta)
    setCurrentMonth((prev) => {
      const next = new Date(prev)
      next.setMonth(next.getMonth() + delta)

      const nextYear = next.getFullYear()
      const nextMonthNum = next.getMonth() + 1
      onCalendarMonthChange?.({ year: nextYear, month: nextMonthNum })

      const rightMonth = new Date(nextYear, nextMonthNum, 1)
      onCalendarMonthChange?.({
        year: rightMonth.getFullYear(),
        month: rightMonth.getMonth() + 1,
      })

      return next
    })
  }

  const selectMonth = (monthIndex, isLeft) => {
    const newDate = new Date(currentMonth.getFullYear(), monthIndex)
    if (isLeft) {
      setCurrentMonth(newDate)
      onCalendarMonthChange?.({ year: newDate.getFullYear(), month: newDate.getMonth() + 1 })
    } else {
      const leftDate = new Date(newDate.getFullYear(), newDate.getMonth() - 1)
      setCurrentMonth(leftDate)
      onCalendarMonthChange?.({ year: leftDate.getFullYear(), month: leftDate.getMonth() + 1 })
      onCalendarMonthChange?.({ year: newDate.getFullYear(), month: newDate.getMonth() + 1 })
    }
    setOpenMonthPicker(null)
  }

  const handleDateSelect = (isLeft) => {
    setOpenMonthPicker((prev) =>
      prev === (isLeft ? LEFT_CALENDAR : RIGHT_CALENDAR)
        ? null
        : isLeft
          ? LEFT_CALENDAR
          : RIGHT_CALENDAR
    )
  }

  const handleFlexClick = (day, date) => {
    const formattedDate = ddmmyyyyformatDate(day, date)

    if (!isSelecting) {
      setIsSelecting(true)
      setDragStartDate(formattedDate)
      setSelectedDates([formattedDate])
      notify([formattedDate], datesState.to || [], CALENDAR_FLEX_MODE)
    } else {
      setIsSelecting(false)
      const sorted = [dragStartDate, formattedDate].sort()
      const dateRange = getDatesBetween(sorted[0], sorted[1])
      const finalDates = dateRange.length > maxDatesRange ? [dragStartDate] : dateRange
      setSelectedDates(finalDates)
      setDragStartDate(null)
      notify(finalDates, datesState.to || [], CALENDAR_FLEX_MODE)
    }
  }

  const handleFlexMouseEnter = (day, date) => {
    if (!isSelecting || !dragStartDate) return

    const formattedDate = ddmmyyyyformatDate(day, date)
    const calStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
    const calEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0)
    const hovered = new Date(date.getFullYear(), date.getMonth(), day)

    if (hovered < calStart || hovered > calEnd) return

    const sorted = [dragStartDate, formattedDate].sort()
    const dateRange = getDatesBetween(sorted[0], sorted[1])
    if (dateRange.length > maxDatesRange) return

    setSelectedDates(dateRange)
  }

  const handleDay = (day, date) => {
    const formattedDate = ddmmyyyyformatDate(day, date)
    const currentTime = Date.now()
    const DOUBLE_CLICK_THRESHOLD = 300

    const newFrom = [...(datesState?.from || [])]
    const newTo = [...(datesState?.to || [])]

    const isDoubleClick =
      lastClick.date === formattedDate && currentTime - lastClick.timestamp < DOUBLE_CLICK_THRESHOLD

    if (isDoubleClick) {
      newFrom[0] = formattedDate
      newTo[0] = formattedDate
      setClickCount(0)
      setLastClick({ date: formattedDate, timestamp: currentTime })
      setSelectedDates([formattedDate])
      notify(newFrom, newTo, mode)
      return
    }

    const nextCount = clickCount + 1

    if (isDeparture) {
      nextCount % 2 === 1 ? (newFrom[0] = formattedDate) : (newTo[0] = formattedDate)
    } else {
      nextCount % 2 === 1 ? (newTo[0] = formattedDate) : (newFrom[0] = formattedDate)
    }

    setClickCount(nextCount)
    setLastClick({ date: formattedDate, timestamp: currentTime })
    setSelectedDates(isDeparture ? newFrom : newTo)
    notify(newFrom, newTo, mode)
  }

  const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)

  const activePriceData = isDeparture ? departPriceData : returPriceData

  const sharedGridProps = {
    availableDepart,
    availableRetur,
    highlightedDates,
    handleFlexClick,
    handleFlexMouseEnter,
    handleDay,
    priceData: activePriceData,
    isDeparture,
    selectedDates,
    mode,
    isSelecting,
    changeMonth,
    dragStartDate,
    currencySymbol, // ← добавлено
  }

  const sharedHeaderProps = {
    months,
    handleDateSelect,
    openMonthPicker,
    changeMonth,
    datePickerSect,
    selectMonth,
  }

  return (
    <div className="w-full overflow-hidden">
      <div className="flex gap-8">
        <div className="flex-1">
          <Header date={currentMonth} isSecondary={false} isLeft={true} {...sharedHeaderProps} />
          <WeekDays weekDays={weekDays} />

          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={`${currentMonth.getMonth()}-${currentMonth.getFullYear()}`}
              custom={direction}
              initial={(custom) => ({ opacity: 0, x: custom > 0 ? 300 : -300 })}
              animate={{ opacity: 1, x: 0 }}
              exit={(custom) => ({ opacity: 0, x: custom > 0 ? -300 : 300 })}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            >
              <Grid date={currentMonth} {...sharedGridProps} />
            </motion.div>
          </AnimatePresence>
        </div>

        {!isMobile && (
          <>
            <div className="h-6 w-px bg-app-base-strong/30" />

            <div className="flex-1">
              <Header date={nextMonth} isSecondary={true} isLeft={false} {...sharedHeaderProps} />
              <WeekDays weekDays={weekDays} />

              <AnimatePresence mode="wait" initial={false} custom={direction}>
                <motion.div
                  key={`${nextMonth.getMonth()}-${nextMonth.getFullYear()}`}
                  custom={direction}
                  initial={(custom) => ({ opacity: 0, x: custom > 0 ? 300 : -300 })}
                  animate={{ opacity: 1, x: 0 }}
                  exit={(custom) => ({ opacity: 0, x: custom > 0 ? -300 : 300 })}
                  transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                >
                  <Grid date={nextMonth} {...sharedGridProps} />
                </motion.div>
              </AnimatePresence>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Calendar
